// Product Fullscreen Modal with Swiper
class ProductFullscreenModal {
  constructor() {
    this.modals = [];
    this.currentModal = null;
    this.zoomLevel = 1;
    this.maxZoom = 3;
    this.init();
    this.setupVariantChangeListener();
  }

  init() {
    // Initialize click handlers for main product images
    this.initImageClickHandlers();
    
    // Handle escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.currentModal) {
        this.closeModal(this.currentModal);
      }
    });

    // Handle window resize to enable/disable modal on mobile
    window.addEventListener('resize', () => {
      this.initImageClickHandlers();
    });
  }

  setupVariantChangeListener() {
    // Listen for variant changes using the theme's pub/sub system
    const setupListener = () => {
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        console.log('Setting up variant change listener for fullscreen modal');
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          console.log('Fullscreen modal variant change event received:', event);
          if (event.data && event.data.variant) {
            this.handleVariantChange(event.data);
          }
        });
      } else {
        console.log('subscribe or PUB_SUB_EVENTS not available yet for fullscreen modal');
      }
    };

    // Try to setup immediately
    setupListener();
    
    // If not available yet, try again after a short delay
    if (!this.variantChangeUnsubscriber) {
      setTimeout(setupListener, 100);
    }
  }

  handleVariantChange(variantData) {
    const { variant, html } = variantData;
    console.log('Fullscreen modal handling variant change:', variant);
    if (!variant) return;

    // Update all open modals
    this.modals.forEach(modalData => {
      this.updateModalForVariant(modalData, variant, html);
    });
  }

  updateModalForVariant(modalData, variant, html) {
    const modal = modalData.modal;
    const mainSwiper = modalData.main;
    const thumbsSwiper = modalData.thumbs;

    console.log('Updating fullscreen modal for variant:', variant.id, 'Featured media:', variant.featured_media);

    if (!mainSwiper) {
      console.log('No main swiper found in fullscreen modal');
      return;
    }

    // If variant has featured media, try to show it
    if (variant.featured_media) {
      const newFeaturedMediaId = variant.featured_media.id;
      console.log('Looking for fullscreen slide with media ID:', newFeaturedMediaId);
      
      // Look specifically for the main slide
      let targetSlide = modal.querySelector(`.product-fullscreen-main-slide[data-media-id="${newFeaturedMediaId}"]`);
      
      console.log('Target fullscreen main slide found:', targetSlide);
      
      if (targetSlide) {
        // Get the slide index from the main swiper
        const slideIndex = Array.from(mainSwiper.slides).indexOf(targetSlide);
        console.log('Fullscreen main slide index:', slideIndex);
        
        if (slideIndex !== -1) {
          // Update main swiper to show the new featured media
          mainSwiper.slideTo(slideIndex, 300);
          console.log('Fullscreen modal slid to index:', slideIndex);
          
          // Update thumbnail swiper if it exists
          if (thumbsSwiper) {
            const thumbSlide = modal.querySelector(`.product-fullscreen-thumbnail-slide[data-media-id="${newFeaturedMediaId}"]`);
            if (thumbSlide) {
              const thumbIndex = Array.from(thumbsSwiper.slides).indexOf(thumbSlide);
              if (thumbIndex !== -1) {
                thumbsSwiper.slideTo(thumbIndex, 300);
                console.log('Fullscreen thumbnail slid to index:', thumbIndex);
              }
            }
          }

          // Update active states
          this.updateModalActiveStates(modal, newFeaturedMediaId);
        } else {
          console.log('Fullscreen main slide index not found');
        }
      } else {
        console.log('Target fullscreen main slide not found, trying first slide');
        // If target slide not found, go to first slide
        if (mainSwiper.slides.length > 0) {
          mainSwiper.slideTo(0, 300);
          const firstSlide = mainSwiper.slides[0];
          if (firstSlide) {
            const firstMediaId = firstSlide.dataset.mediaId;
            this.updateModalActiveStates(modal, firstMediaId);
          }
        }
      }
    } else {
      console.log('No featured media, going to first slide in fullscreen modal');
      // If no featured media, go to first slide
      if (mainSwiper.slides.length > 0) {
        mainSwiper.slideTo(0, 300);
        
        if (thumbsSwiper && thumbsSwiper.slides.length > 0) {
          thumbsSwiper.slideTo(0, 300);
        }
        
        // Update active states to first slide
        const firstSlide = mainSwiper.slides[0];
        if (firstSlide) {
          const firstMediaId = firstSlide.dataset.mediaId;
          this.updateModalActiveStates(modal, firstMediaId);
        }
      }
    }
  }

  updateModalActiveStates(modal, featuredMediaId) {
    // Update main slides
    modal.querySelectorAll('.product-fullscreen-main-slide').forEach(slide => {
      slide.classList.remove('is-active');
      if (slide.dataset.mediaId === featuredMediaId.toString()) {
        slide.classList.add('is-active');
      }
    });

    // Update thumbnail slides
    modal.querySelectorAll('.product-fullscreen-thumbnail-slide').forEach(thumb => {
      thumb.classList.remove('is-active');
      if (thumb.dataset.mediaId === featuredMediaId.toString()) {
        thumb.classList.add('is-active');
      }
    });
  }

  initImageClickHandlers() {
    // Check if device is mobile
    const isMobile = window.innerWidth <= 768;
    
    // Remove existing click handlers first
    const mainImages = document.querySelectorAll('.product-main-image');
    const mainMediaContainers = document.querySelectorAll('.product-main-media');
    
    // Remove existing event listeners by cloning and replacing elements
    mainImages.forEach(image => {
      const newImage = image.cloneNode(true);
      image.parentNode.replaceChild(newImage, image);
    });
    
    mainMediaContainers.forEach(container => {
      const newContainer = container.cloneNode(true);
      container.parentNode.replaceChild(newContainer, container);
    });
    
    // Don't add click handlers on mobile
    if (isMobile) {
      return;
    }

    // Find all main product images that should trigger the modal
    const newMainImages = document.querySelectorAll('.product-main-image');
    
    newMainImages.forEach(image => {
      image.addEventListener('click', (e) => {
        e.preventDefault();
        const productId = this.getProductIdFromImage(image);
        if (productId) {
          this.openModal(productId, image);
        }
      });
    });

    // Also handle clicks on the main media container
    const newMainMediaContainers = document.querySelectorAll('.product-main-media');
    newMainMediaContainers.forEach(container => {
      container.addEventListener('click', (e) => {
        e.preventDefault();
        const productId = this.getProductIdFromContainer(container);
        if (productId) {
          this.openModal(productId, container);
        }
      });
    });
  }

  getProductIdFromImage(image) {
    // Try to find product ID from various sources
    const productContainer = image.closest('[data-product-id]');
    if (productContainer) {
      return productContainer.dataset.productId;
    }

    const productInfo = image.closest('product-info');
    if (productInfo) {
      return productInfo.dataset.productId;
    }

    // Fallback: try to extract from URL or other sources
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('id') || this.extractProductIdFromUrl();
  }

  getProductIdFromContainer(container) {
    const productContainer = container.closest('[data-product-id]');
    if (productContainer) {
      return productContainer.dataset.productId;
    }

    const productInfo = container.closest('product-info');
    if (productInfo) {
      return productInfo.dataset.productId;
    }

    return this.extractProductIdFromUrl();
  }

  extractProductIdFromUrl() {
    const path = window.location.pathname;
    const match = path.match(/\/products\/[^\/]+/);
    if (match) {
      // Extract product handle and try to find the product
      const handle = match[0].split('/').pop();
      // For now, we'll use a simple approach - you might need to adjust this
      return handle;
    }
    return null;
  }

  openModal(productId, clickedImage = null) {
    const modalId = `product-fullscreen-modal-${productId}`;
    const modal = document.getElementById(modalId);
    
    if (!modal) {
      console.warn(`Modal not found for product ID: ${productId}`);
      return;
    }

    this.currentModal = modal;
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Initialize Swiper instances
    this.initSwiperInstances(modal);

    // Sync with clicked image if provided
    if (clickedImage) {
      this.syncModalWithClickedImage(modal, clickedImage);
    }

    // Add event listeners
    this.addModalEventListeners(modal);

    // Focus management
    modal.focus();
  }

  syncModalWithClickedImage(modal, clickedImage) {
    // Get the media ID from the clicked image
    const clickedSlide = clickedImage.closest('.product-main-slide');
    if (!clickedSlide) {
      console.log('Could not find clicked slide');
      return;
    }

    const clickedMediaId = clickedSlide.dataset.mediaId;
    console.log('Syncing modal with clicked media ID:', clickedMediaId);

    // Find the corresponding slide in the modal
    const modalSlide = modal.querySelector(`.product-fullscreen-main-slide[data-media-id="${clickedMediaId}"]`);
    if (!modalSlide) {
      console.log('Could not find corresponding modal slide');
      return;
    }

    // Get the modal swiper instances
    const modalData = this.getModal(modal);
    if (!modalData || !modalData.main) {
      console.log('Modal swiper not found');
      return;
    }

    const mainSwiper = modalData.main;
    const thumbsSwiper = modalData.thumbs;

    // Get the slide index
    const slideIndex = Array.from(mainSwiper.slides).indexOf(modalSlide);
    if (slideIndex === -1) {
      console.log('Slide index not found');
      return;
    }

    console.log('Sliding modal to index:', slideIndex);

    // Slide to the clicked image
    mainSwiper.slideTo(slideIndex, 0); // No animation for initial sync

    // Sync thumbnail swiper if it exists
    if (thumbsSwiper) {
      const thumbSlide = modal.querySelector(`.product-fullscreen-thumbnail-slide[data-media-id="${clickedMediaId}"]`);
      if (thumbSlide) {
        const thumbIndex = Array.from(thumbsSwiper.slides).indexOf(thumbSlide);
        if (thumbIndex !== -1) {
          thumbsSwiper.slideTo(thumbIndex, 0);
        }
      }
    }

    // Update active states
    this.updateModalActiveStates(modal, clickedMediaId);
  }

  closeModal(modal) {
    if (!modal) return;

    modal.style.display = 'none';
    document.body.style.overflow = '';
    this.currentModal = null;
    this.zoomLevel = 1;

    // Reset zoom
    const mainImage = modal.querySelector('.product-fullscreen-main-image');
    if (mainImage) {
      mainImage.classList.remove('zoomed');
      mainImage.style.transform = 'scale(1)';
    }

    // Destroy Swiper instances
    this.destroySwiperInstances(modal);
  }

  initSwiperInstances(modal) {
    const mainSwiperEl = modal.querySelector('[data-fullscreen-main-swiper]');
    const thumbsSwiperEl = modal.querySelector('[data-fullscreen-thumbs-swiper]');

    if (!mainSwiperEl || !thumbsSwiperEl) return;

    // Initialize thumbs swiper
    const thumbsSwiper = new Swiper(thumbsSwiperEl, {
      spaceBetween: 10,
      slidesPerView: 'auto',
      freeMode: true,
      watchSlidesProgress: true,
      watchSlidesVisibility: true,
      breakpoints: {
        768: {
          slidesPerView: 6,
        },
        320: {
          slidesPerView: 4,
        }
      }
    });

    // Initialize main swiper
    const mainSwiper = new Swiper(mainSwiperEl, {
      spaceBetween: 10,
      navigation: {
        nextEl: modal.querySelector('.product-fullscreen-nav-next'),
        prevEl: modal.querySelector('.product-fullscreen-nav-prev'),
      },
      thumbs: {
        swiper: thumbsSwiper,
      },
      on: {
        slideChange: function() {
          const activeSlide = this.slides[this.activeIndex];
          const mediaType = activeSlide.querySelector('[data-media-type]')?.dataset.mediaType;
          
          // Reset zoom level for new slide
          this.zoomLevel = 1;
          
          // Reset all images to default zoom
          modal.querySelectorAll('.product-fullscreen-main-image').forEach(img => {
            img.style.transform = 'scale(1)';
            img.classList.remove('zoomed');
          });
          
          // Handle video autoplay
          if (mediaType === 'video') {
            const video = activeSlide.querySelector('video');
            if (video) {
              video.play().catch(e => console.log('Video autoplay prevented'));
            }
          }
          
          // Handle 3D model
          if (mediaType === 'model') {
            const model = activeSlide.querySelector('model-viewer');
            if (model) {
              model.play();
            }
          }
          
          // Ensure thumbnail sync and active state
          const mediaId = activeSlide.dataset.mediaId;
          const correspondingThumb = modal.querySelector(`.product-fullscreen-thumbnail-slide[data-media-id="${mediaId}"]`);
          if (correspondingThumb) {
            // Remove active class from all thumbnails
            modal.querySelectorAll('.product-fullscreen-thumbnail-slide').forEach(thumb => {
              thumb.classList.remove('swiper-slide-active');
            });
            // Add active class to corresponding thumbnail
            correspondingThumb.classList.add('swiper-slide-active');
          }
        }
      }
    });

    // Store instances
    modal.mainSwiper = mainSwiper;
    modal.thumbsSwiper = thumbsSwiper;

    // Add to modals array
    this.modals.push({
      modal,
      main: mainSwiper,
      thumbs: thumbsSwiper
    });
  }

  destroySwiperInstances(modal) {
    if (modal.mainSwiper && modal.mainSwiper.destroy) {
      modal.mainSwiper.destroy(true, true);
    }
    if (modal.thumbsSwiper && modal.thumbsSwiper.destroy) {
      modal.thumbsSwiper.destroy(true, true);
    }

    // Remove from modals array
    this.modals = this.modals.filter(m => m.modal !== modal);
  }

  addModalEventListeners(modal) {
    // Close button
    const closeBtn = modal.querySelector('.product-fullscreen-modal__close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeModal(modal));
    }

    // Overlay click to close
    const overlay = modal.querySelector('.product-fullscreen-modal__overlay');
    if (overlay) {
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) {
          this.closeModal(modal);
        }
      });
    }

    // Zoom controls
    const zoomInBtn = modal.querySelector('.product-fullscreen-modal__zoom-in');
    const zoomOutBtn = modal.querySelector('.product-fullscreen-modal__zoom-out');

    if (zoomInBtn) {
      zoomInBtn.addEventListener('click', () => this.zoomIn(modal));
    }

    if (zoomOutBtn) {
      zoomOutBtn.addEventListener('click', () => this.zoomOut(modal));
    }

    // Thumbnail clicks
    const thumbnails = modal.querySelectorAll('.product-fullscreen-thumbnail');
    thumbnails.forEach((thumbnail, index) => {
      thumbnail.addEventListener('click', () => {
        const mediaId = thumbnail.closest('.product-fullscreen-thumbnail-slide').dataset.mediaId;
        const mainSlide = modal.querySelector(`.product-fullscreen-main-slide[data-media-id="${mediaId}"]`);
        
        if (mainSlide && modal.mainSwiper) {
          const slideIndex = Array.from(modal.mainSwiper.slides).indexOf(mainSlide);
          if (slideIndex !== -1) {
            modal.mainSwiper.slideTo(slideIndex);
          }
        }
        
        // Update active state for thumbnails
        modal.querySelectorAll('.product-fullscreen-thumbnail-slide').forEach(thumb => {
          thumb.classList.remove('swiper-slide-active');
        });
        thumbnail.closest('.product-fullscreen-thumbnail-slide').classList.add('swiper-slide-active');
      });
    });
  }

  zoomIn(modal) {
    const activeSlide = modal.querySelector('.product-fullscreen-main-swiper .swiper-slide-active');
    const mainImage = activeSlide ? activeSlide.querySelector('.product-fullscreen-main-image') : null;
    if (mainImage && this.zoomLevel < this.maxZoom) {
      this.zoomLevel = Math.min(this.zoomLevel + 0.5, this.maxZoom);
      mainImage.style.transform = `scale(${this.zoomLevel})`;
      mainImage.classList.add('zoomed');
    }
  }

  zoomOut(modal) {
    const activeSlide = modal.querySelector('.product-fullscreen-main-swiper .swiper-slide-active');
    const mainImage = activeSlide ? activeSlide.querySelector('.product-fullscreen-main-image') : null;
    if (mainImage && this.zoomLevel > 1) {
      this.zoomLevel = Math.max(this.zoomLevel - 0.5, 1);
      if (this.zoomLevel === 1) {
        mainImage.classList.remove('zoomed');
      }
      mainImage.style.transform = `scale(${this.zoomLevel})`;
    }
  }



  // Public method to get modal instances
  getModal(modal) {
    return this.modals.find(m => m.modal === modal);
  }

  // Public method to destroy all modals
  destroy() {
    // Clean up variant change listener
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }

    this.modals.forEach(({ modal }) => {
      this.closeModal(modal);
    });
    this.modals = [];
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Check if Swiper is available
  if (typeof Swiper !== 'undefined') {
    window.productFullscreenModal = new ProductFullscreenModal();
  } else {
    console.warn('Swiper is not loaded. Product fullscreen modal will not work.');
  }
});

// Handle dynamic content loading (for AJAX-loaded content)
document.addEventListener('shopify:section:load', (event) => {
  if (window.productFullscreenModal) {
    // Re-initialize image click handlers for new content
    setTimeout(() => {
      window.productFullscreenModal.initImageClickHandlers();
    }, 100);
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductFullscreenModal;
}



